import React, { useContext, useMemo, useRef, useState, useCallback, useEffect } from 'react';
import _ from 'lodash';
import moment from 'moment';
import './OfficeClock.scss';

import { OfficeContext } from './Office';
import OfficeClockTimeReadout from './OfficeClockTimeReadout';
import OfficeClockPresets from './OfficeClockPresets';
import OfficeClockSlider from './OfficeClockSlider';
import OfficeClockDayPicker from './OfficeClockDayPicker';
import {
  TIME_RANGE,
  SLOT_MINUTES,
  TOTAL_SLOTS,
  getOfficePermissions,
  buildWeekdays,
  buildQuickPresets,
  slotToTimeStr,
  slotToLabel,
  timeToSlot
} from '../../helpers/office_helpers';

/*
 * OfficeClock — the unified time bar at the top of the Office tab.
 *
 * Time model:
 *   - The day is divided into 30-minute "slots" (SLOT_MINUTES) from 7 AM to 9 PM (TIME_RANGE).
 *   - TOTAL_SLOTS = 28 slots. timeToSlot/slotToTimeStr convert between "HH:mm" strings and slot indices.
 *   - Two modes: "point" (single time) and "range" (start + end). Range is set by dragging or presets.
 *
 * Local state vs context:
 *   - localStart/localEnd track the drag state for responsive UI during mouse movement.
 *   - Commits to context (setSelectedTime/setSelectedEndTime) are debounced by 250ms during drag,
 *     then flushed immediately on mouse-up for a crisp final value.
 */
const DEBOUNCE_MS = 250;

const OfficeClock = () => {
  const ctx = useContext(OfficeContext);
  const {
    announcements,
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    selectedEndTime,
    setSelectedEndTime,
    currentUser,
    myBookings
  } = ctx;
  const trackRef = useRef(null);
  const debounceRef = useRef(null);

  const [localStart, setLocalStart] = useState(() => timeToSlot(selectedTime));
  const [localEnd, setLocalEnd] = useState(() => (selectedEndTime ? timeToSlot(selectedEndTime) : null));
  const [dragMode, setDragMode] = useState(null);
  const [dragAnchor, setDragAnchor] = useState(null);
  const [hoverPct, setHoverPct] = useState(null);
  const [hoverSlot, setHoverSlot] = useState(null);
  const bookedDates = useMemo(() => new Set((myBookings || []).map(b => moment(b.starts_at).format('YYYY-MM-DD'))), [myBookings]);

  useEffect(() => {
    if (!dragMode) {
      setLocalStart(timeToSlot(selectedTime));
      setLocalEnd(selectedEndTime ? timeToSlot(selectedEndTime) : null);
    }
  }, [selectedTime, selectedEndTime, dragMode]);

  const permissions = getOfficePermissions(currentUser);
  const maxAdvanceDays = permissions?.max_advance_days || 7;
  const daysScrollRef = useRef(null);

  // Only reach for announcement days the user can actually select, so the strip never ends on a disabled pill.
  const selectableAnnouncements = announcements.filter(a => a.allowEarlyBooking || moment(a.date).diff(moment().startOf('day'), 'days') <= maxAdvanceDays);
  const furthestAnnouncementDate = _.maxBy(selectableAnnouncements, 'date')?.date || null;
  const allDays = useMemo(() => buildWeekdays(50, furthestAnnouncementDate), [furthestAnnouncementDate]);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = daysScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = daysScrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  const scrollDays = dir => {
    if (!daysScrollRef.current) return;
    daysScrollRef.current.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' });
  };

  // ─── Slider logic ───

  const hourMarkers = useMemo(() => {
    const markers = [];
    for (let h = TIME_RANGE.start; h <= TIME_RANGE.end; h++) {
      const slotIdx = ((h - TIME_RANGE.start) * 60) / SLOT_MINUTES;
      const pct = (slotIdx / TOTAL_SLOTS) * 100;
      markers.push({ hour: h, pct, full: moment({ hour: h }).format('hA') });
    }
    return markers;
  }, []);

  const slotFromX = useCallback(clientX => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    return Math.round(Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * TOTAL_SLOTS);
  }, []);

  const pctFromX = useCallback(clientX => {
    if (!trackRef.current) return null;
    const rect = trackRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
  }, []);

  const handleTrackHover = useCallback(
    e => {
      setHoverPct(pctFromX(e.clientX));
      setHoverSlot(slotFromX(e.clientX));
    },
    [dragMode, pctFromX]
  );

  const handleTrackLeave = useCallback(() => {
    if (!dragMode) setHoverPct(null);
    setHoverSlot(null);
  }, [dragMode]);

  const commit = useCallback(
    (start, end) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSelectedTime(slotToTimeStr(start));
        setSelectedEndTime(end !== null && end !== start ? slotToTimeStr(end) : null);
      }, DEBOUNCE_MS);
    },
    [setSelectedTime, setSelectedEndTime]
  );

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleDown = useCallback(
    e => {
      const slot = slotFromX(e.clientX || e.touches?.[0]?.clientX || 0);
      setDragAnchor(slot);
      setLocalStart(slot);
      setLocalEnd(null);
      setDragMode('point');
      setHoverPct(null);
      e.preventDefault();
    },
    [slotFromX]
  );

  const handleMove = useCallback(
    e => {
      if (!dragMode) return;
      const slot = slotFromX(e.clientX || e.touches?.[0]?.clientX || 0);
      if (dragAnchor === null) return;
      const diff = Math.abs(slot - dragAnchor);
      if (diff >= 1) {
        const lo = Math.min(slot, dragAnchor);
        const hi = Math.max(slot, dragAnchor);
        setLocalStart(lo);
        setLocalEnd(hi);
        setDragMode('range');
        commit(lo, hi);
      } else {
        setLocalStart(dragAnchor);
        setLocalEnd(null);
        commit(dragAnchor, null);
      }
    },
    [dragMode, dragAnchor, slotFromX, commit]
  );

  const handleUp = useCallback(() => {
    clearTimeout(debounceRef.current);
    setSelectedTime(slotToTimeStr(localStart));
    setSelectedEndTime(localEnd !== null && localEnd !== localStart ? slotToTimeStr(localEnd) : null);
    setDragMode(null);
    setDragAnchor(null);
  }, [localStart, localEnd, setSelectedTime, setSelectedEndTime]);

  useEffect(() => {
    if (!dragMode) return;
    const move = e => handleMove(e);
    const up = () => handleUp();
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, [dragMode, handleMove, handleUp]);

  // ─── Computed values ───

  const isToday = selectedDate === moment().format('YYYY-MM-DD');

  const hasRange = localEnd !== null && localEnd !== localStart;
  const startPct = (localStart / TOTAL_SLOTS) * 100;
  const endPct = hasRange ? (localEnd / TOTAL_SLOTS) * 100 : startPct;

  const dayPrefix = (() => {
    if (isToday) return null;
    const sel = moment(selectedDate);
    const diff = sel.diff(moment().startOf('day'), 'days');
    if (diff === 1) return 'Tomorrow,';
    return `${sel.format('dddd')},`;
  })();

  const now = moment();
  const nowMinutes = now.hours() * 60 + now.minutes() - TIME_RANGE.start * 60;
  const totalMinutes = (TIME_RANGE.end - TIME_RANGE.start) * 60;
  const nowPct = isToday && nowMinutes >= 0 && nowMinutes <= totalMinutes ? (nowMinutes / totalMinutes) * 100 : null;

  const quickPresets = useMemo(() => buildQuickPresets(isToday, now), [isToday, selectedDate]);

  const clearRange = () => {
    const nowPreset = quickPresets.find(p => p.label === 'Now');
    setSelectedEndTime(null);
    setLocalEnd(null);
    if (isToday && nowPreset) setSelectedTime(slotToTimeStr(nowPreset.start));
    else setSelectedTime('09:00');
  };

  const dayBookings = useMemo(() => myBookings.filter(b => moment(b.starts_at).format('YYYY-MM-DD') === selectedDate), [myBookings, selectedDate]);

  const presetBookingMatch = useCallback(
    preset => {
      const pStart = TIME_RANGE.start * 60 + preset.start * SLOT_MINUTES;
      const pEnd = TIME_RANGE.start * 60 + preset.end * SLOT_MINUTES;
      return dayBookings.find(b => {
        const bStart = moment(b.starts_at).hours() * 60 + moment(b.starts_at).minutes();
        const bEnd = moment(b.ends_at).hours() * 60 + moment(b.ends_at).minutes();
        return bStart === pStart && bEnd === pEnd;
      });
    },
    [dayBookings]
  );

  const unmatchedBookings = useMemo(() => {
    const matchedIds = new Set();
    quickPresets.forEach(p => {
      const match = presetBookingMatch(p);
      if (match) matchedIds.add(match.id);
    });
    return dayBookings.filter(b => !matchedIds.has(b.id));
  }, [dayBookings, quickPresets, presetBookingMatch]);

  return (
    <div className='office-clock'>
      <div className='clock-top-row'>
        <div className='clock-left-section'>
          <OfficeClockDayPicker
            allDays={allDays}
            announcements={announcements}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            setSelectedTime={setSelectedTime}
            setSelectedEndTime={setSelectedEndTime}
            maxAdvanceDays={maxAdvanceDays}
            bookedDates={bookedDates}
            daysScrollRef={daysScrollRef}
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
            scrollDays={scrollDays}
          />
          <OfficeClockPresets
            quickPresets={quickPresets}
            hasRange={hasRange}
            localStart={localStart}
            localEnd={localEnd}
            presetBookingMatch={presetBookingMatch}
            unmatchedBookings={unmatchedBookings}
            clearRange={clearRange}
            setLocalStart={setLocalStart}
            setLocalEnd={setLocalEnd}
            setSelectedTime={setSelectedTime}
            setSelectedEndTime={setSelectedEndTime}
            isToday={isToday}
          />
        </div>
      </div>

      <div className='clock-controls'>
        <OfficeClockTimeReadout
          key={`${selectedDate}-${hasRange}`}
          hasRange={hasRange}
          localStart={localStart}
          localEnd={localEnd}
          dayPrefix={dayPrefix}
          selectedTime={selectedTime}
          selectedEndTime={selectedEndTime}
          setLocalStart={setLocalStart}
          setLocalEnd={setLocalEnd}
          setSelectedTime={setSelectedTime}
          setSelectedEndTime={setSelectedEndTime}
        />
        <OfficeClockSlider
          trackRef={trackRef}
          handleDown={handleDown}
          handleTrackHover={handleTrackHover}
          handleTrackLeave={handleTrackLeave}
          hourMarkers={hourMarkers}
          hoverPct={hoverPct}
          hoverSlot={hoverSlot}
          nowPct={nowPct}
          hasRange={hasRange}
          startPct={startPct}
          endPct={endPct}
          dragMode={dragMode}
          localStart={localStart}
          clearRange={clearRange}
        />
      </div>
    </div>
  );
};

export default OfficeClock;
